import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { EPersona } from '@norfolk-southern/accessns-components';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ICustomer, ILocation } from '@ruby/modules/customer-select/models/customer.interface';
import { IThirdPartyEcr, IThirdPartyLOA, IThirdPartySearchCompany } from '@ruby/modules/third-party/models/third-party.interface';
import { selectThirdPartyContractState } from '@ruby/modules/third-party/store';
import { fromThirdPartyAuthorization, ThirdPartyAuthorizationActions } from '@ruby/modules/third-party/store/third-party-authorization.index';
import { fromThirdPartyCompanies } from '@ruby/modules/third-party/store/third-party-companies.index';
import { fromThirdPartyCore } from '@ruby/modules/third-party/store/third-party-core.index';
import { fromThirdPartyStepper } from '@ruby/modules/third-party/store/third-party-stepper.index';
import { ERole } from '@ruby/shared/enums/roles.enums';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-third-party-authorization-container',
  template: `
    <ng-third-party-authorization
      *ngIf="(store$ | async) as store"
      [locationsByRole]="store.authorizations.locationsByLOB"
      [ecrData]="store.authorizations.ecrData"
      [lineOfBusinessList]="store.authorizations.lob"
      [loadingLOB]="store.authorizations.loading"
      [isEdit]="store.stepper.isEdit"
      [isNewLob]="store.stepper.isNewLOA"
      [apTransp]="store.authorizations.apTransp"
      [apEcr]="store.authorizations.apEcr"
      [apHljs]="store.authorizations.apHljs"
      [apRating]="store.authorizations.apRating"
      [apRevenue]="store.authorizations.apRevenue"
      [apRevenueClaims]="store.authorizations.apRevenueClaims"
      [apStorageAndLift]="store.authorizations.apStorageAndLift"
      [lineOfBusiness]="lineOfBusiness"
      (selectedEcr)="selectEcr($event)"
      (selectedLob)="selectLob($event)"
      (selectedRating)="selectRating($event)"
      (selectedHljs)="selectHljs($event)"
      (selectedRevenue)="selectRevenue($event)"
      (selectedRevenueClaims)="selectRevenueClaims($event)"
      (selectedStorageAndLift)="selectStorageAndLift($event)"
      (selectedTransp)="selectTransportation($event)"
      (authStatus)="authStatus($event)">
    </ng-third-party-authorization>
  `
})
export class ThirdPartyAuthorizationContainerComponent {
  customerCompany?: ICustomer;
  providerCompany?: IThirdPartySearchCompany;
  lineOfBusiness?: string;
  store$: Observable<{
    core: fromThirdPartyCore.State;
    authorizations: fromThirdPartyAuthorization.State;
    companies: fromThirdPartyCompanies.State;
    stepper: fromThirdPartyStepper.State;
  }>;

  constructor(private store: Store<fromThirdPartyAuthorization.State | fromThirdPartyCompanies.State>, private utilsSvc: UtilsService) {
    this.store$ = this.store.select(selectThirdPartyContractState).pipe(
      tap((st: {
        core: fromThirdPartyCore.State;
        authorizations: fromThirdPartyAuthorization.State;
        companies: fromThirdPartyCompanies.State;
        stepper: fromThirdPartyStepper.State;
      }) => {
        this.lineOfBusiness = st.authorizations.selectedLOB;
        if (st.companies.customerCompany && st.companies.providerCompany) {
          if (this.providerCompany?.eid !== st.companies.providerCompany.eid ||
            this.customerCompany?.customerIdentifier !== st.companies.customerCompany.customerIdentifier) {
            if (st.core.loa && st.stepper.isEdit) {
              this.store.dispatch(ThirdPartyAuthorizationActions.setLineOfBusiness({
                data: {
                  description: this.utilsSvc.mappingRoleToTitle(this.mapNameToRole(st.core.loa.lineOfBusiness)),
                  lineOfBusiness: st.core.loa.lineOfBusiness,
                  role: this.mapNameToRole(st.core.loa.lineOfBusiness)
                }
              }));
              this.setupApplications(st.core.loa);
            } else {
              this.store.dispatch(ThirdPartyAuthorizationActions.loadAvailableLineOfBusiness({
                companyEId: st.companies.customerCompany.customerIdentifier,
                tplCompanyEId: st.companies.providerCompany.eid
              }));
            }
          }
        }
        this.customerCompany = st.companies.customerCompany;
        this.providerCompany = st.companies.providerCompany;
      })
    );
  }

  /**
   * Select Ecr
   *
   * @summary The following method dispatch a boolean to change the status of ECR application
   * @param $event: boolean
   * @returns void
   */
  selectEcr($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setEcr({ data: $event }));
  }

  /**
   * Select LOB
   *
   * @summary The following method dispatch a boolean to change the Line Of Business application
   * @param $event: string
   * @returns void
   */
  selectLob($event: string): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setSelectedLOB({ data: $event }));
  }

  /**
   * Select Rating
   *
   * @summary The following method dispatch a boolean to change the status of Rating application
   * @param $event: boolean
   * @returns void
   */
  selectRating($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setRating({ data: $event }));
  }

  /**
   * Select HLJS
   *
   * @summary The following method dispatch a boolean to change the status of HLJS application
   * @param $event: boolean
   * @returns void
   */
  selectHljs($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setHljs({ data: $event }));
  }

  /**
   * Select Revenue
   *
   * @summary The following method dispatch a boolean to change the status of Revenue application
   * @param $event: boolean
   * @returns void
   */
  selectRevenue($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setRevenue({ data: $event }));
  }

  /**
   * Select Revenue Claims
   *
   * @summary The following method dispatch a boolean to change the status of Revenue Claims application
   * @param $event: boolean
   * @returns void
   */
  selectRevenueClaims($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setRevenueClaims({ data:  $event ? $event : false }));
  }

  /**
   * Select Storage And Lift
   *
   * @summary The following method dispatch a boolean to change the status of Storage And Lift application
   * @param $event: boolean
   * @returns void
   */
  selectStorageAndLift($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setStorageAndLift({ data: $event }));
  }

  /**
   * Select Transportation
   *
   * @summary The following method dispatch a boolean to change the status of Transportation application
   * @param $event: boolean
   * @returns void
   */
  selectTransportation($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setTransportation({ data: $event }));
  }

  /**
   * Auth Status
   *
   * @summary The following method dispatch a boolean to change the authorization form status
   * @param $event: boolean
   * @returns void
   */
  authStatus($event: boolean): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setAuthorizationStatus({ data: $event }));
  }

  /**
   * Map Name To Role
   *
   * @summary The following method maps the line of business to its corresponding Role
   * @param name: string
   * @returns ERole
   */
  mapNameToRole(name: string): ERole {
    switch (name) {
      case 'Industrial Product':
      case 'INDUSTRIAL_PRODUCT':
        return ERole.ROLE_IP;
      case 'Short Line':
      case 'SHORT_LINE':
        return ERole.ROLE_SL;
      case 'Intermodal':
      case 'INTERMODAL':
        return ERole.ROLE_IM;
      case 'Offline':
      case 'OFFLINE':
        return ERole.ROLE_OFFLINE;
      case 'Unit Train':
      case 'UNIT_TRAIN':
        return ERole.ROLE_UT;
      case 'DRAY':
        return ERole.ROLE_Dray;
      default:
        return ERole.ROLE_OFFLINE;
    }
  }

  private setupApplications(loa: IThirdPartyLOA): void {
    this.selectTransportation(loa.apTransportation);
    if (loa.apTransportation && loa.locations) {
      this.setLocations(loa.locations, loa.lineOfBusiness);
    }
    this.selectRevenue(loa.apRevenue);
    this.selectRevenueClaims(loa.apRevenueClaims);
    this.selectStorageAndLift(loa.apStorageAndLift);
    this.selectHljs(loa.apHljs);
    this.selectRating(loa.apRating);
    this.selectEcr(loa.apEmptyCarRequest);
    if (loa.apEmptyCarRequest && loa.ecrDetails) {
      this.setEcrData(loa.ecrDetails);
    }
  }

  private setLocations(loc: Array<ILocation>, lineOfBusiness: string): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setSelectedLocations({
      data: {
        type: this.utilsSvc.getTypeRoleMapping(this.mapNameToRole(lineOfBusiness)),
        customer: {
          customerIdentifier: '',
          locations: loc,
          customerName: '',
          groupId: '',
          id: '',
          primarySixIds: [],
          scac: '',
          groupName: '',
          type: {
            description: '',
            name: '' as EPersona,
            role: ''
          }
        }
      }
    }));
  }

  private setEcrData(ecr: IThirdPartyEcr): void {
    this.store.dispatch(ThirdPartyAuthorizationActions.setEcrData({
      data: {
        equipmentTypes: ecr.equipmentTypes,
        railRoadCompany: {
          key: ecr.railRoadCompanyEId,
          value: ecr.railRoadCompanyEId
        },
        originState: ecr.railRoadLocState,
        originCity: {
          key: ecr.railRoadLocCity,
          value: ecr.railRoadLocCity
        },
        stccDescription: {
          key: ecr.stcc,
          value: ecr.stcc
        }
      }
    }));
  }
}
